import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import RadialChart from 'components/@common/Charts/RadialChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { WdtClassroomModuleProgressData } from 'types/schema-generated-types';
import { ModuleType } from '../../../../types';
import { merge } from 'lodash';
import { PrintViewContext } from 'components/ReportsModule/providers/PrintViewProvider';
import {
  BAR_CHART_COMMON_OPTIONS,
  MODULE_LENGTH_MAP,
  MODULE_LEVELS_COLOR_MAP
} from '../../../../utils/report-wdt-utils';

interface ActivitiesCompletedCharacterCardProps {
  module: ModuleType;
  totalStudentCount: number;
  reportData: WdtClassroomModuleProgressData[];
  loading: boolean;
}

const ActivitiesCompletedCharacterCard: React.FC<ActivitiesCompletedCharacterCardProps> = ({
  module,
  totalStudentCount,
  reportData,
  loading
}) => {
  const { t } = useTranslation('Reports');
  const { cardChartWidth } = useContext(PrintViewContext);

  const moduleReportData = reportData.filter(item => item.module === module);

  const categories = ['1 Star', '2 Stars', '3 Stars'];

  const constFactor = totalStudentCount ? 100 / (MODULE_LENGTH_MAP[module] * totalStudentCount) : 1;

  const series: any[] = [
    moduleReportData
      .filter(item => item.level === 1)
      .map(item => item.charactersCompletedCount * constFactor),
    moduleReportData
      .filter(item => item.level === 2)
      .map(item => item.charactersCompletedCount * constFactor),
    moduleReportData
      .filter(item => item.level === 3)
      .map(item => item.charactersCompletedCount * constFactor)
  ];

  const options = merge({}, BAR_CHART_COMMON_OPTIONS, {
    colors: MODULE_LEVELS_COLOR_MAP[module],
    xaxis: { categories },
    tooltip: { enabled: false },
    plotOptions: {
      radialBar: {
        hollow: { size: '25%' },
        // track: { show: false },
        dataLabels: {
          name: { show: false },
          total: { show: false },
          value: {
            offsetY: 5,
            formatter: (val: number): string => Math.round(val) + '%'
          }
        }
      }
    },
    legend: {
      show: true,
      position: 'bottom',
      formatter: (_: any, { seriesIndex }: any): string => categories[seriesIndex]
    }
  });

  return (
    <ReportCard
      title={t(`${t(module, { ns: 'ModuleType' })} Activities Completed`)}
      helpText={t(`wdtClassroomActivitiesCompleted-${module}`, { ns: 'ReportsHelpCommon' })}
      loading={loading}
      reportData={reportData}
    >
      <RadialChart height={250} width={cardChartWidth} series={series} options={options} />
    </ReportCard>
  );
};

export default React.memo(ActivitiesCompletedCharacterCard);
